import { amber, red, blue, teal } from '@material-ui/core/colors';
import { Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import { useCompanyMaster, useSnackbarInstance } from '@lib/common';
import { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { DraggableRowsTable } from '@components/draggable-rows-table';
import { ColoredChipIconButton } from '@components/colored-elements';
import { ImDrawer2, TrashFill, BiEdit } from '@icon/react-icons';
import { createContextQuick } from '@components/quick-context';
import { BadgesPicker } from '../company-badges/BadgesPicker';
// import { TypesContainer } from '../company-types';
import { createFormField } from '../../common';
import getLoadingMessage from '@lib/common/loading-messages';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import { useBadgeMasterAutccomplete } from '@lib/common';
import { CompanyBadge } from '@lib/company';
import styled from 'styled-components';
import { thumbStack } from '@lib/common/css';
import { ColoredAvatar, ColoredChip } from '@components/colored-elements';

export const CompanyBadgesLoader = ({ value = null, onChange = null, similarClick = null }) => {
  // const { master: badges, loading } = useCompanyMaster('badges');
  const { master: badgesMaster, loading } = useBadgeMasterAutccomplete(true);
  const filteredBadges = useMemo(() => {
    if (value && value.length && badgesMaster && badgesMaster.length) {
      return badgesMaster.filter(
        s => !value.find(v => v.badge_master_vtr_id === s.badge_master_vtr_id)
      );
    }
    return badgesMaster;
  }, [value, badgesMaster]);

  const { enqueue } = useSnackbarInstance();

  const onBadgeAdd = useCallback(
    (badge: CompanyBadge) => {
      if (value && onChange) {
        if (!value.find(v => v.badge_master_vtr_id === badge.badge_master_vtr_id)) {
          onChange([...value, badge]);
        } else {
          enqueue(
            <>
              Badge <strong>{badge.badge_name}</strong> is already added.
            </>,
            { variant: 'error' }
          );
        }
      } else if (!value) {
        // Adding new badge and the value is not usable.
        onChange([badge]);
      }
    },
    [value, onChange]
  );

  const onBadgeDelete = (badge: CompanyBadge) => {
    if (value && onChange) {
      const idx = value.findIndex(v => v.badge_master_vtr_id === badge.badge_master_vtr_id);
      if (idx >= 0) {
        const nextValue = [...value];
        nextValue.splice(idx, 1);
        onChange(nextValue);
      }
    }
  };

  const onEditBadge = (badge: CompanyBadge, idx?: number) => {
    if (value && onChange) {
      // const idx = value.findIndex(v => v.badge_master_vtr_id === badge.badge_master_vtr_id);
      // if (idx >= 0) {
      //   const nextValue = [...value];
      //   nextValue.splice(idx, 1);
      //   onChange(nextValue);
      // }
      if (idx >= 0) {
        const nextValue = [...value];
        nextValue[idx] = badge;
        onChange(nextValue);
      }
    }
  };

  const BadgeColumn: Column<CompanyBadge>[] = useMemo(
    () => [
      {
        Header: '#',
        id: 'serial',
        accessor: (_row, idx) => idx + 1,
        width: 35,
      },
      {
        Header: 'Badge Name',
        id: 'badge_name',
        accessor: row => row.badge_name,
        width: 200,
      },
      {
        Header: 'Badge Publish',
        id: 'publish',
        accessor: row => (row.publish ? 'True' : 'False'),
        width: 85,
      },
      {
        Header: 'Badge Attributes',
        id: 'parameter_values_json',
        accessor: row => {
          const attributeEntries = Object.entries(row.parameter_values_json);
          return (
            <div>
              {attributeEntries.map(eachAttribute => (
                <span style={{ paddingRight: '5px' }}>
                  <Tooltip
                    title={`${eachAttribute[0]} : ${eachAttribute[1]}`}
                    key={eachAttribute[0]}>
                    <ColoredChip
                      colored={teal[500]}
                      size="small"
                      label={`${eachAttribute[0]} : ${eachAttribute[1]}`}
                    />
                  </Tooltip>
                </span>
              ))}
            </div>
          );
        },
        width: 200,
      },
      {
        Header: 'Action',
        id: 'actions',
        width: 80,
        Cell({ row }) {
          const { deleteBadge, editBadge, onSimilarClick = null } = useActions();
          const onDeleteClick = () => deleteBadge(row.original);
          const onEditClick = () => {
            setEachBadgeEdit(true);
          };
          const editBadgeFunction = (badge, idx) => {
            editBadge(badge, idx);
            setEachBadgeEdit(false);
          };
          const [eachBadgeEdit, setEachBadgeEdit] = useState(false);
          return (
            (row.original.source == 'Vitrina' || row.original.source == 'DELI') && (
              <>
                <Tooltip title="Edit">
                  <span style={{ paddingRight: '5px' }}>
                    <ColoredChipIconButton
                      onClick={onEditClick}
                      pointer
                      colored={blue[900]}
                      size="small"
                      disabled={loading}
                      icon={<BiEdit />}
                    />
                    {!loading && badgesMaster && (
                      <BadgesPicker
                        badges={filteredBadges}
                        onBadgeAdd={onBadgeAdd}
                        onBadgeEdit={editBadgeFunction}
                        badgeEditState={eachBadgeEdit}
                        editBadgeScenario={true}
                        editBadgeIndex={row.index}
                        editBadgeData={row.original}
                        setEachBadgeEdit={setEachBadgeEdit}
                      />
                    )}
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <ColoredChipIconButton
                      onClick={onDeleteClick}
                      pointer
                      colored={red[900]}
                      size="small"
                      icon={<TrashFill />}
                    />
                  </span>
                </Tooltip>
              </>
            )
          );
        },
      },
    ],
    [loading, badgesMaster, filteredBadges]
  );

  return (
    <BadgesContainer>
      <Grid container spacing={4} justify="center" alignItems="center">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            Manage Badges
          </Typography>
        </Grid>
        <Grid item xs />
        <Grid item>
          {!loading && badgesMaster && (
            // <BadgesPicker badges={filteredBadges} onBadgeAdd={onBadgeAdd} /> //////////////
            <BadgesPicker badges={filteredBadges} onBadgeAdd={onBadgeAdd} />
          )}
          {loading && (
            <Typography variant="body2" color="textSecondary">
              {getLoadingMessage()}
            </Typography>
          )}
        </Grid>
      </Grid>
      {value && value.length ? (
        <Paper className="fixed-height">
          <ProvideEdit
            deleteBadge={onBadgeDelete}
            editBadge={onEditBadge}
            onSimilarClick={similarClick}>
            <DraggableRowsTable
              getRowId={getBadgeRowId}
              onChange={onChange}
              columns={BadgeColumn}
              data={value}
              stickyHeader
              size="small"
              showDragIcon={false}
              // stopDrag={true}
            />
          </ProvideEdit>
        </Paper>
      ) : (
        EmptyContent
      )}
    </BadgesContainer>
  );
};

const EmptyContent = (
  <Paper className="fixed-height no-data">
    <span>
      <ImDrawer2 />
      <br />
      <Typography>
        Click on <strong>Add</strong> button
        <br />
        and start managing Badges.
      </Typography>
    </span>
  </Paper>
);

const [ProvideEdit, useActions] = createContextQuick<{
  deleteBadge?: (badge: CompanyBadge) => any;
  editBadge?: (badge: CompanyBadge, idx?: number) => any;
  onSimilarClick?: (badge: CompanyBadge) => any;
}>();
const getBadgeRowId = row => row.badge_master_vtr_id;

export const CompanyBadgesField = createFormField(CompanyBadgesLoader, {
  defaultValue: [],
  passProps: true,
});

const BadgesContainer = styled.div`
  padding: 10px 5px;
  background: #f5f5f5;
  table {
    td:nth-of-type(1) {
      padding-left: 10px;
      width: 40px;
    }
    th:nth-of-type(1) {
      padding-left: 10px;
      width: 40px;
    }
    // td:nth-of-type(2) {
    //   width: 35px;
    // }
    td:last-child {
      width: 80px;
    }
  }
  .drag-icon {
    cursor: grab;
  }
  .fixed-height {
    ${thumbStack}
    margin-top: 6px;
    margin-bottom: 6px;
    height: 40vh;
    position: relative;
    overflow: auto;
    .MuiTableContainer-root {
      ${thumbStack}
      height: inherit;
      overflow: inherit;
    }
    &.no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      span {
        text-align: center;
        svg {
          font-size: 4em;
          color: #ccc;
        }
      }
    }
  }
`;
